import { addToolState, getToolForElement, getToolState } from 'app/CornerstoneTools';
import makeMoveHandlerChain from 'app/CornerstoneTools/makeMoveHandlerChain';
import { EventWithCornerstoneDetail, InteractionType } from 'app/CornerstoneTools/types';
import { ToolOptions, BaseAnnotation } from 'cornerstone-tools';
import { updateImage } from 'cornerstone-core';

const isMeasurementCreationOngoing = (element: HTMLElement, toolName: string) =>
  getToolState(element, toolName)?.data.some(({ completed }: { completed: boolean }) => !completed);

type ChainingToolBuilderProps<AnnotationMeasurement extends BaseAnnotation> = {
  name: string;
  createNewMeasurement: (eventData: EventWithCornerstoneDetail['detail']) => AnnotationMeasurement;
  handlesKeys: string[];
  maximumToolData: number;
  options?: ToolOptions;
};

export default class ChainingToolBuilder<AnnotationMeasurement extends BaseAnnotation> {
  name: string;
  createNewMeasurement: (eventData: EventWithCornerstoneDetail['detail']) => AnnotationMeasurement;
  maximumToolData: number;
  handlesKeys: string[];
  options?: ToolOptions;
  currentToolChainCallbacks: ReturnType<typeof makeMoveHandlerChain> | undefined;

  constructor({
    name,
    createNewMeasurement,
    handlesKeys,
    maximumToolData,
    options,
  }: ChainingToolBuilderProps<AnnotationMeasurement>) {
    this.name = name;
    this.createNewMeasurement = createNewMeasurement;
    this.maximumToolData = maximumToolData;
    this.handlesKeys = handlesKeys;
    this.options = options;
  }

  addNewMeasurement = (evt: EventWithCornerstoneDetail, interactionType: InteractionType) => {
    const eventData = evt.detail;
    const { element } = eventData;
    if (isMeasurementCreationOngoing(element, this.name)) return;

    evt.preventDefault();
    evt.stopPropagation();

    const isMaximumToolDrawn =
      this.maximumToolData > 0 &&
      getToolState(element, this.name)?.data?.length >= this.maximumToolData;
    if (isMaximumToolDrawn) return;

    const measurementData = this.createNewMeasurement(eventData);

    addToolState(element, this.name, measurementData);

    const [firstHandleKey, ...remainingHandlesKeys] = this.handlesKeys;
    updateImage(element);
    this.currentToolChainCallbacks = makeMoveHandlerChain(
      element,
      this,
      measurementData,
      interactionType,
      measurementData.handles[firstHandleKey],
      remainingHandlesKeys
    );
  };

  checkToolCreationCancelled = (element: HTMLElement, data: BaseAnnotation) => {
    const toolInstance = getToolForElement(element, this.name);
    if (!data.completed && toolInstance.mode !== 'active' && this.currentToolChainCallbacks) {
      this.currentToolChainCallbacks.cancelFn();
      return true;
    }
    return false;
  };
}
